import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/api';
import { broadcastLogout, listenCrossTabLogout } from '../utils/crossTabAuthSync';

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(() => {
    const saved = localStorage.getItem('admin_user');
    try {
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => {
    const saved = localStorage.getItem('admin_user');
    const token = localStorage.getItem('admin_token') || localStorage.getItem('super_admin_token');
    // Only block initial render if a token exists but adminUser is not yet in localStorage
    return !!token && !saved;
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('super_admin_token');
    if (token) {
      api.get('/api/superadmin/me/')
        .then(res => {
          if (res.data?.success && res.data?.user) {
            setAdminUser(res.data.user);
            localStorage.setItem('admin_user', JSON.stringify(res.data.user));
          } else if (res.data && res.data.success === false) {
            setAdminUser(null);
            localStorage.removeItem('admin_user');
          }
        })
        .catch(err => {
          console.warn('SuperAdmin session refresh failed:', err);
          if (err.response && (err.response.status === 401 || err.response.status === 403)) {
            setAdminUser(null);
            localStorage.removeItem('admin_user');
            localStorage.removeItem('admin_token');
            localStorage.removeItem('super_admin_token');
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }

    const unsubscribe = listenCrossTabLogout(() => {
      setAdminUser(null);
      if (window.location.pathname.startsWith('/superadmin') && !window.location.pathname.includes('/login')) {
        window.location.href = '/superadmin/login';
      }
    });
    return unsubscribe;
  }, []);

  const refreshAdminUser = async () => {
    try {
      const res = await api.get('/api/superadmin/me/');
      if (res.data?.success && res.data?.user) {
        setAdminUser(res.data.user);
        localStorage.setItem('admin_user', JSON.stringify(res.data.user));
        return res.data.user;
      }
    } catch (err) {
      console.warn('Failed to refresh admin user:', err);
    }
    return null;
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/superadmin/login/', { email, password });
      if (response.data?.mfa_required) {
        return {
          success: true,
          mfa_required: true,
          mfa_token: response.data.mfa_token,
          masked_email: response.data.masked_email || email,
          email: response.data.email || email
        };
      }

      const { user, token } = response.data;
      setAdminUser(user);
      localStorage.setItem('admin_user', JSON.stringify(user));
      if (token) {
        localStorage.setItem('admin_token', token);
        localStorage.setItem('super_admin_token', token);
      }
      return { success: true, mfa_required: false };
    } catch (error) {
      console.error('Admin Login failed:', error);
      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  const verifyMfa = async (mfaToken, otp) => {
    try {
      const response = await api.post('/api/superadmin/verify-mfa/', { mfa_token: mfaToken, otp });
      if (response.data?.require_password_change) {
        return {
          success: true,
          require_password_change: true,
          reset_token: response.data.reset_token,
          email: response.data.email
        };
      }

      const { user, token } = response.data;
      setAdminUser(user);
      localStorage.setItem('admin_user', JSON.stringify(user));
      if (token) {
        localStorage.setItem('admin_token', token);
        localStorage.setItem('super_admin_token', token);
      }
      localStorage.setItem('is_superuser', 'true');
      localStorage.setItem('is_staff', 'true');
      return { success: true };
    } catch (error) {
      console.error('2FA OTP verification failed:', error);
      return { success: false, error: error.response?.data?.error || '2FA OTP Verification failed' };
    }
  };

  const setPermanentPassword = async (email, resetToken, newPassword, confirmPassword) => {
    try {
      const response = await api.post('/api/superadmin/set-permanent-password/', {
        email,
        reset_token: resetToken,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      const { user, token } = response.data;
      if (user) {
        setAdminUser(user);
        localStorage.setItem('admin_user', JSON.stringify(user));
      }
      if (token) {
        localStorage.setItem('admin_token', token);
        localStorage.setItem('super_admin_token', token);
      }
      localStorage.setItem('is_superuser', 'true');
      localStorage.setItem('is_staff', 'true');
      return { success: true };
    } catch (error) {
      console.error('Set permanent password failed:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to set permanent password.' };
    }
  };

  const resendMfa = async (mfaToken) => {
    try {
      const response = await api.post('/api/superadmin/resend-mfa/', { mfa_token: mfaToken });
      return { success: true, message: response.data?.message };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Could not resend OTP code' };
    }
  };

  const logout = () => {
    setAdminUser(null);
    broadcastLogout('super_admin');
  };

  const value = {
    adminUser,
    loading,
    refreshAdminUser,
    login,
    verifyMfa,
    resendMfa,
    setPermanentPassword,
    logout,
    isAdminAuthenticated: !!adminUser
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
